/**
 * loadShellBootstrapContext — async orchestrator for the shell bootstrap sequence.
 *
 * Coordinates two parallel data lanes and composes them into a shell context:
 *   1. Plugin lane  — `readPluginLane(bridge)` (onboarding.state / app.bootstrap)
 *   2. Session lane — `readBrowserGatewaySessionLane(bridge)`
 *
 * The session lane owns the V1 browser-side gateway bootstrap:
 *   gateway.identity.get (bridge, local only)
 *   POST /v1/installations/register (browser -> gateway, only on fresh install)
 *   gateway.installation.persist (bridge, local only, only after register)
 *   gateway.challenge.sign (bridge, local only)
 *   POST /v1/auth/login/challenge (browser -> gateway)
 *
 * Both lanes are started concurrently.
 * Errors in individual lanes are absorbed by the lane normalizers; this function
 * never throws.
 *
 * @param {object} bridge - BridgeClient instance (sendMessageWithResponse)
 * @param {object} [options] - Test/dev injection surface (fetchImpl, signal)
 * @returns {Promise<object>} Shell bootstrap context from composeShellBootstrapContext
 */

import { readPluginLane } from '../../home/loader/readPluginLane';
import { readBrowserGatewaySessionLane } from './readBrowserGatewaySessionLane';
import { composeShellBootstrapContext } from './composeShellBootstrapContext';

export async function loadShellBootstrapContext(bridge, options = {}) {
  // Each lane normalizer absorbs its own errors, so Promise.all cannot reject.
  const [pluginLane, sessionLane] = await Promise.all([
    readPluginLane(bridge),
    readBrowserGatewaySessionLane(bridge, options),
  ]);

  return composeShellBootstrapContext(pluginLane, sessionLane);
}
